<template>
    <div class="report-template-step">
        <form ref="form" @submit.prevent>
            <!-- Name Input -->
            <label>Name <span class="text-danger">*</span></label>
            <CInput v-model="formData.name" class="mb-4" placeholder="Input schedule report name" required />

            <!-- Schedule Section -->
            <h5 style="font-weight: 700;">Schedule</h5>
            <div class="schedule-section mt-3 mb-4">
                <div class="d-flex align-items-center mb-3" style="gap: 24px;">
                    <div class="custom-control custom-radio">
                        <input type="radio" id="weekly" value="weekly" v-model="formData.schedule_type"
                            class="custom-control-input">
                        <label class="custom-control-label" for="weekly">Weekly</label>
                    </div>
                    <div class="custom-control custom-radio">
                        <input type="radio" id="monthly" value="monthly" v-model="formData.schedule_type"
                            class="custom-control-input">
                        <label class="custom-control-label" for="monthly">Monthly</label>
                    </div>
                </div>
                <p class="my-0" style="font-size: 13px; font-weight: 700;">
                    {{ scheduleDescription }}
                </p>
            </div>
            <v-divider class="ma-0 mb-4" color="#D7D7D7"></v-divider>

            <!-- Select Report Template Section -->
            <h5 style="font-weight: 700;">Select Report Template</h5>
            <div class="mt-3">
                <div class="d-flex align-items-start" style="gap: 16px;">
                    <div>
                        <label>Category <span class="text-danger">*</span></label>
                        <div style="width: 190px;">
                            <CSelectNew v-model="formData.category" :options="reportCategoryList" item-text="label"
                                item-value="value" placeholder="Select category" required />
                        </div>
                    </div>
                    <div style="width: 100%;">
                        <label>Template Name <span class="text-danger">*</span></label>
                        <div class="template-select-row">
                            <CSelectNew class="template-select" v-model="formData.template_id" :options="reportTemplates" item-text="name"
                                item-value="id" placeholder="Select template" :disabled="!formData.category" required />
                            <div class="edit-template-button" v-if="formData.template_id">
                                <a :href="`/dashboard/template_builder/${formData.template_id}`">
                                    <span class="mdi mdi-pencil-outline"></span> Edit Template
                                </a>
                            </div>
                        </div>

                    </div>
                </div>
            </div>
        </form>

        <div class="preview-container" :style="{ backgroundColor: !selectedTemplate ? '#F4F4F4' : 'transparent' }">
            <!-- Empty State - Show when no template selected -->
            <div v-if="!selectedTemplate" class="empty-state text-center py-5">
                <p class="mb-0" style="color: #3F3F3F;">Select Report Template to show preview</p>
            </div>

            <!-- Filter Section (show when template selected) -->
            <div v-if="selectedTemplate">
                <div class="">
                    <h5 style="font-weight: 700;">Filter</h5>
                    <p  class="mb-4 mt-2"><i>Some filters shown below are pre-configured in the report template.</i></p>
                </div>
                <ColumnFilter class="mb-4" :filter-options="selectedTemplate.filter_options || []"
                    :saved-filters="resolvedSavedFilters" :organization-id="organizationId"
                    :management-business-department="managementBusinessDepartment"
                    :reference-date="nextRunDate"
                    @update:filters="handleFilterUpdate" />
                <div v-if="hasDynamicDateFilter" class="dynamic-date-filter-banner mb-4">
                    Dynamic date filters automatically update based on the scheduled send date.
                    Exported data may change each time this report is sent.
                    Total <b>{{ formattedDataCount }}</b> data based on the applied filters.
                </div>
            </div>

            <!-- Column Preview Section -->
            <div v-if="selectedTemplate" class="mt-4">
                <ColumnDetail :template-id="formData.template_id" :schedule-id="initialData?.id"
                    :filters="formData.filters" @run-report="handleRunReport" />
            </div>
        </div>
    </div>
</template>

<script>
import momentTz from 'moment-timezone';
import CInput from '../../c-input.vue';
import CSelectNew from '../../c-select-new.vue';
import ColumnFilter from '../ColumnFilter.vue';
import ColumnDetail from '../ColumnDetail.vue';
import { mapGetters, mapState } from 'vuex';

const SCHEDULE_TIME_ZONE = 'Etc/GMT+5';

export default {
    name: 'ReportTemplate',
    components: {
        CInput,
        CSelectNew,
        ColumnFilter,
        ColumnDetail
    },
    props: {
        initialData: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            // Form Data
            formData: {
                name: '',
                category: '',
                template_id: null,
                schedule_type: 'weekly',
                schedule_day: 'monday',
                schedule_time: '09:00',
                filters: {},
            },

            // Static Options
            reportCategoryList: [
                { value: 'Report::Case', label: 'Cases' },
                { value: 'Report::Client', label: 'Clients' },
                { value: 'Report::Join', label: 'Join (Cases & Clients)' },
                { value: 'Report::Sales', label: 'Sales' },
                { value: 'Report::Ticket', label: 'Tickets' },
            ],

            weekDays: [
                { value: 'monday', label: 'Monday' },
                { value: 'tuesday', label: 'Tuesday' },
                { value: 'wednesday', label: 'Wednesday' },
                { value: 'thursday', label: 'Thursday' },
                { value: 'friday', label: 'Friday' },
                { value: 'saturday', label: 'Saturday' },
                { value: 'sunday', label: 'Sunday' },
            ],

            monthDays: Array.from({ length: 28 }, (_, i) => ({
                value: i + 1,
                label: `Day ${i + 1}`
            })),
            templateListParams: {
                sort: 'name_asc',
            },


        }
    },
    mounted() {
        if (this.initialData && this.initialData.template_id) {
            this.loadFromInitialData();
        } else if (this.selectedReport) {
            this.getReportList();
        }
    },
    methods: {
        handleRunReport() {
            this.$emit('run-report');
        },
        async loadFromInitialData() {
            // Populate form from scheduled report data (edit mode)
            const data = this.initialData;
            this.formData.name = data.name || '';
            this.formData.category = data.category || '';
            // NOTE:
            // `schedule_type`, `schedule_day`, and `schedule_time` are FE-only inputs.
            // Backend currently stores only `frequency` and computed `next_run_at/last_run_at`.
            // We keep these fields to drive UI copy and preview calculations on the client.
            this.formData.schedule_type = data.schedule_type || 'weekly';
            this.formData.schedule_day = data.schedule_day || 'monday';
            this.formData.schedule_time = data.schedule_time || '09:00';
            this.formData.filters = data.filters || {};

            // Fetch template list for the category
            if (data.category) {
                await this.$store.dispatch('reportSchedules/getReportTemplates', {
                    category: data.category,
                    params: this.templateListParams,
                });
            }
            // Set template_id after list is loaded
            this.formData.template_id = data.template_id;
        },
        async getReportList() {
            this.formData.category = this.selectedReport.type;
            await this.$store.dispatch('reportSchedules/getReportTemplates', {
                category: this.formData.category,
                params: this.templateListParams,
            });
            this.formData.template_id = this.selectedReport.id;

            // Initialize filters from selectedReport if available
            if (this.selectedReport.filters) {
                this.formData.filters = { ...this.selectedReport.filters };
            }
        },

        handleFilterUpdate(filters) {
            // Use spread to create a new object reference for Vue reactivity
            this.formData.filters = { ...filters };
        },
        // Validation method - exposed for parent to call via ref
        // Validation method - exposed for parent to call via ref
        validate() {
            // Use native browser validation
            // reportValidity() returns true if form is valid, false otherwise.
            // It also triggers the native bubbles for invalid fields.
            if (this.$refs.form && this.$refs.form.reportValidity) {
                return this.$refs.form.reportValidity();
            }
            return true;
        }
    },
    computed: {
        ...mapState({
            organizationId: state => state.reportTemplates.organizationId,
            managementBusinessDepartment: state => state.reportTemplates.managementBusinessDepartment,
        }),
        ...mapGetters({
            reportTemplates: 'reportSchedules/reportTemplates',
            dataCount: 'reportList/dataCount',
        }),
        resolvedSavedFilters() {
            if (this.initialData && this.initialData.id) {
                return this.formData.filters || {};
            }

            if (this.formData.filters && Object.keys(this.formData.filters).length > 0) {
                return this.formData.filters;
            }

            return (this.selectedTemplate && this.selectedTemplate.filters) ? this.selectedTemplate.filters : {};
        },
        hasDynamicDateFilter() {
            return Array.isArray(this.formData.filters?.dynamic_filters)
                && this.formData.filters.dynamic_filters.length > 0;
        },
        formattedDataCount() {
            if (this.dataCount == null) return '-';
            return this.dataCount.toLocaleString('en-US');
        },
        ...mapState({
            // Get full template data from reportTemplates store (fetched via getReportTemplateById)
            reportTemplateData: state => state.reportTemplates.reportTemplate,
            selectedReport: state => state.selectedReport, // From Rails (ID mode)
        }),
        selectedTemplate() {
            if (!this.formData.template_id) return null;

            // ID mode: Use selectedReport from Rails if available and matches
            if (this.selectedReport && this.selectedReport.id === this.formData.template_id) {
                return this.selectedReport;
            }

            // New mode: Use full template data fetched via getReportTemplateById
            if (this.reportTemplateData && this.reportTemplateData.id === this.formData.template_id) {
                return this.reportTemplateData;
            }

            // Fallback: Find basic info from reportTemplates (may not have filter_options)
            const template = this.reportTemplates.find(t => t.id === this.formData.template_id);
            return template || null;
        },
        nextRunDate() {
            const type = this.formData.schedule_type || 'weekly';
            const time = this.formData.schedule_time || '09:00';
            const day = this.formData.schedule_day || 'monday';

            const now = momentTz().tz(SCHEDULE_TIME_ZONE);
            const timeParts = time.split(':');
            const hours = parseInt(timeParts[0], 10) || 9;
            const minutes = parseInt(timeParts[1], 10) || 0;

            let nextDate = momentTz().tz(SCHEDULE_TIME_ZONE).hours(hours).minutes(minutes).seconds(0).milliseconds(0);

            if (type === 'weekly') {
                const targetIsoDay = now.clone().day(day).isoWeekday();
                nextDate = nextDate.isoWeekday(targetIsoDay);
                if (nextDate.isSame(now, 'day') || nextDate.isBefore(now)) {
                    nextDate.add(1, 'week');
                }
            } else {
                nextDate.date(1);
                if (nextDate.isBefore(now)) {
                    nextDate.add(1, 'month');
                }
            }

            return nextDate;
        },
        scheduleDescription() {
            const type = this.formData.schedule_type || 'weekly';
            const dayValue = this.formData.schedule_day || 'monday';
            const time = this.formData.schedule_time || '09:00';
            const [hours, minutes] = time.split(':');
            const hourValue = parseInt(hours, 10);
            const minuteValue = parseInt(minutes, 10);
            const hourDisplay = Number.isFinite(hourValue) ? (hourValue % 12 || 12) : 9;
            const minuteDisplay = Number.isFinite(minuteValue) ? minuteValue.toString().padStart(2, '0') : '00';
            const ampm = Number.isFinite(hourValue) && hourValue >= 12 ? 'PM' : 'AM';
            const timeLabel = `${hourDisplay}:${minuteDisplay} ${ampm} CST`;
            const startLabel = this.nextRunDate ? this.nextRunDate.format('MMMM D, YYYY') : '';

            if (type === 'weekly') {
                const dayName = dayValue.charAt(0).toUpperCase() + dayValue.slice(1);
                return `The Report will be sent every ${dayName} at ${timeLabel}${startLabel ? ` - (Start on ${startLabel})` : ''}`;
            }

            return `The Report will be sent monthly on every 1st day of the month at ${timeLabel}${startLabel ? ` - (Start on ${startLabel})` : ''}`;
        }
    },
    watch: {
        initialData: {
            immediate: true,
            deep: true,
            handler(val) {
                if (val && val.template_id) {
                    this.loadFromInitialData();
                }
            }
        },
        'formData.category': {
            handler(newVal, oldVal) {
                if (newVal !== oldVal) {
                    this.formData.template_id = null;
                    // Fetch templates for this category
                    if (newVal) {
                        this.$store.dispatch('reportSchedules/getReportTemplates', {
                            category: newVal,
                            params: this.templateListParams,
                        });
                    }
                }
            }
        },
        'formData.template_id': {
            handler(newVal) {
                if (newVal) {
                    // Fetch template details when a template is selected (new mode only)
                    // In ID mode, selectedReport is already populated from Rails
                    if (!this.selectedReport || this.selectedReport.id !== newVal) {
                        this.$store.dispatch('reportTemplates/getReportTemplateById', newVal);
                        this.$store.dispatch('reportTemplates/getAllReportFields', newVal);
                    }
                }
            }
        },
        // Watch for reportTemplateData changes to load saved filters
        reportTemplateData: {
            handler(newVal, oldVal) {
                // Only update filters when template data changes and matches our selection
                // Skip if this is the same template data (prevents infinite loop)
                if (newVal && newVal.id === this.formData.template_id && (!oldVal || oldVal.id !== newVal.id)) {
                    if (this.initialData && this.initialData.id) {
                        return;
                    }
                    // Don't overwrite if we already have filters from ID mode (selectedReport)
                    if (this.selectedReport && this.selectedReport.id === newVal.id) {
                        return; // ID mode - filters already loaded from getReportList()
                    }
                    // New mode - load saved filters from the fetched template
                    if (newVal.filter) {
                        this.formData.filters = { ...newVal.filter };
                    }
                }
            }
        },
        formData: {
            deep: true,
            handler(val) {
                this.$emit('update:formData', val);
            }
        }
    }
}
</script>

<style scoped lang="scss">
.preview-container {
    margin-top: 24px;
    border: 1px solid #D7D7D7;
    border-radius: 8px;
    padding: 15px;
}

.dynamic-date-filter-banner {
    border: 1px solid #FA9917;
    border-radius: 6px;
    background-color: #FFF7EB;
    color: #3F3F3F;
    font-size: 14px;
    line-height: 20px;
    padding: 8px 16px;
}

.template-select-row {
    width: 650px;
    display: flex;
    align-items: center;
    gap: 16px;
}

.template-select {
    flex: 1 1 auto;
    min-width: 0;
}

.edit-template-button {
    flex: 0 0 auto;
    white-space: nowrap;
}

.report-template-step {
    background-color: #fff;
    padding-bottom: calc(140px + env(safe-area-inset-bottom));
}

.filter-chip {
    background-color: #fff;
    border: 1px solid #D7D7D7;
    border-radius: 8px;
    padding: 8px 16px;
    font-size: 14px;
    display: flex;
    align-items: center;
    gap: 8px;
    cursor: pointer;

    &:hover {
        background-color: #f5f5f5;
    }
}

.column-preview-container {
    border: 1px solid #D7D7D7;
    border-radius: 8px;
    overflow: hidden;

    .table {
        margin-bottom: 0;
    }

    .column-header {
        background-color: #F4F4F4;
        font-size: 13px;
        font-weight: 600;
        padding: 12px;
    }

    td {
        padding: 16px;
    }
}

.schedule-section {
    border-radius: 8px;
}

@media (max-width: 768px) {
    .report-template-step {
        padding-bottom: calc(160px + env(safe-area-inset-bottom));
    }
}
</style>
